import { useContext } from 'react';
import type { FC } from 'react';

import InlineOptions from '../../inline-options';
import Divider from '../../divider';
import Select from '../../select';
import Chart from '../../chart';
import { ScatterPlotContext } from '../../../../providers/widgets/scatter-plot-provider/scatter-plot-provider';
import { RangeEnum } from '../../../../model/range.enum';
import { volSurfaces, tenors } from '../../../../providers/widgets/scatter-plot-provider/constants';
import { useScatterPlotChartConfig } from './hooks/use-scatter-plot-chart-config';

import './scatter-plot-styles.scss';

const getValues = () => {
  const result: any[] = [{ label: 'Spot', value: 'spot' }];

  tenors.forEach((tenor) => {
    const makeSurfaceLeaves = (predicate: (s: string) => boolean, type?: 'PUT' | 'CALL') =>
      volSurfaces.filter(predicate).map((surface) => ({
        label: surface,
        value: type ? `${surface}_${type}_${tenor}` : `${surface}_${tenor}`,
      }));

    const entry = {
      label: tenor,
      value: tenor,
      items: [
        {
          label: 'ATMF',
          value: `ATMF_${tenor}`,
        },
        {
          label: 'Put',
          value: `PUT_${tenor}`,
          items: makeSurfaceLeaves((s) => s.endsWith('P'), 'PUT'),
        },
        {
          label: 'Call',
          value: `CALL_${tenor}`,
          items: makeSurfaceLeaves((s) => s.endsWith('C'), 'CALL'),
        },
        {
          label: 'Risk Reversal',
          value: `RISK_REVERSAL_${tenor}`,
          items: makeSurfaceLeaves((s) => s.endsWith('RR')),
        },
        {
          label: 'Butterfly',
          value: `BUTTERFLY_${tenor}`,
          items: makeSurfaceLeaves((s) => s.endsWith('FLY')),
        },
      ],
    };
    result.push(entry);
  });

  return result;
};

const formatSelectionLabel = (value: string): string => {
  if (!value) return '';
  if (value === 'spot') return 'Spot';
  const parts = value.split('_');
  if (parts[0] === 'ATMF' && parts.length === 2) return `${parts[1]} ATMF`;
  if (parts.length === 3 && (parts[1] === 'PUT' || parts[1] === 'CALL')) {
    return `${parts[2]} ${parts[0]}`;
  }
  if (parts[0] === 'RISK' && parts[1] === 'REVERSAL' && parts.length === 3) return `${parts[2]} RR`;
  if (parts[0] === 'RISK' && parts[1] === 'REVERSAL') return `${parts[2] ?? ''} RR`;
  if (parts[0] === 'RISK_REVERSAL' && parts.length === 2) return `${parts[1]} RR`;
  if (parts[0] === 'BUTTERFLY' && parts.length === 2) return `${parts[1]} FLY`;
  if (parts.length === 2) return `${parts[1]} ${parts[0]}`;
  if (parts.length === 2 && (parts[0] === 'PUT' || parts[0] === 'CALL')) {
    return `${parts[1]} ${parts[0]}`;
  }
  return value.replace(/_/g, ' ');
};

const ScatterPlotContent: FC = () => {
  const {
    token: currency,
    range,
    setRange,
    xAxis,
    setXAxis,
    yAxis,
    setYAxis,
    data,
    loading,
  } = useContext(ScatterPlotContext);

  const { chartSeries, xAxisConfig, yAxisConfig, tooltipConfig } = useScatterPlotChartConfig({
    data,
    xAxis,
    yAxis,
    currency,
  });

  const rangeOptions = [
    RangeEnum.D1,
    RangeEnum.D5,
    RangeEnum.M1,
    RangeEnum.M3,
    RangeEnum.M6,
    RangeEnum.Y1,
  ].map((r) => ({
    label: r,
    value: r,
  }));

  const axisOptions = getValues();

  return (
    <div className="scatter-plot-container">
      <div className="scatter-plot-header">
        <InlineOptions
          options={rangeOptions}
          selectedOption={range}
          onOptionChange={(val) => setRange(val as RangeEnum)}
        />
        <Divider />
        <div className="scatter-plot-axis-controls">
          <div className="axis-control">
            <span className="axis-label">X-Axis</span>
            <Select
              options={axisOptions}
              selectedValues={[xAxis]}
              onChange={(values) => setXAxis(values[0])}
              multiSelect={false}
              placeholder="Select X-Axis"
              renderSelectedLabel={(_sel) => formatSelectionLabel(xAxis)}
            />
          </div>
          <Divider />
          <div className="axis-control">
            <span className="axis-label">Y-Axis</span>
            <Select
              options={axisOptions}
              selectedValues={[yAxis]}
              onChange={(values) => setYAxis(values[0])}
              multiSelect={false}
              placeholder="Select Y-Axis"
              renderSelectedLabel={(_sel) => formatSelectionLabel(yAxis)}
            />
          </div>
        </div>
      </div>

      <div className="scatter-plot-chart">
        <Chart
          series={chartSeries}
          xAxis={xAxisConfig}
          yAxis={yAxisConfig}
          tooltip={tooltipConfig}
          isLoading={loading}
        />
      </div>
    </div>
  );
};

export default ScatterPlotContent;
